import React, { useCallback, useMemo } from 'react';

import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import { toDayJs } from '~/services/TimeService';

import { CatalogRoom, ConflictResolution, RateConflictInfo, StopsellDateRange, StopsellRoom } from '~/types/stopsell';

import { countNights, formatDateRange } from '~/utils/stopsellDateUtils';
import { pluralizeToString } from '~/utils/stringUtils';

import StopsellRateRow from './StopsellRateRow';

interface Props {
  room: StopsellRoom;
  selected: Array<boolean>;
  catalogRooms: Array<CatalogRoom>;
  selectedRoomTypeId: string;
  blackoutDates: Array<StopsellDateRange>;
  catalogRoomRates: Array<{ id: string; name: string }>;
  rateConflicts: Array<Array<RateConflictInfo>>;
  rateResolutions: Record<string, ConflictResolution>;
  onChange: (dateIndex: number, value: boolean) => void;
  onToggleAll: (value: boolean) => void;
  onRoomTypeChange: (roomTypeId: string) => void;
  onResolve: (dateIndex: number, rateId: string, status: ConflictResolution) => void;
  onUndo: (dateIndex: number, rateId: string) => void;
}

export default function StopsellRoomGroup({
  room,
  selected,
  catalogRooms,
  selectedRoomTypeId,
  blackoutDates,
  catalogRoomRates,
  rateConflicts,
  rateResolutions,
  onChange,
  onToggleAll,
  onRoomTypeChange,
  onResolve,
  onUndo,
}: Props) {
  const selectedCount = selected.filter(Boolean).length;
  const allSelected = selected.length > 0 && selectedCount === selected.length;
  const anySelected = selectedCount > 0;
  const indeterminate = anySelected && !allSelected;

  const handleToggleAll = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleAll(!allSelected);
    },
    [allSelected, onToggleAll],
  );

  const isMapped = !!selectedRoomTypeId;

  const today = useMemo(() => toDayJs(new Date().toISOString().slice(0, 10)), []);
  const futureBlackoutDates = blackoutDates.filter((d) => !toDayJs(d.end).isBefore(today, 'day'));
  const existingLabel =
    futureBlackoutDates.length > 0 ? futureBlackoutDates.map((d) => formatDateRange(d.start, d.end)).join(', ') : null;

  return (
    <Accordion defaultExpanded variant="outlined" disableGutters>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ bgcolor: 'grey.50' }}
        component="div"
        role="button"
        tabIndex={0}
      >
        <Stack direction="row" alignItems="center" gap={1} sx={{ flex: 1, mr: 1 }}>
          <Checkbox
            checked={allSelected}
            indeterminate={indeterminate}
            onChange={(e) => {
              e.stopPropagation();
              onToggleAll(e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            size="small"
          />
          <Stack sx={{ flex: 1, minWidth: 0 }} gap={0.5}>
            <Typography fontWeight={500} variant="body2">
              {room.name}
            </Typography>
            {catalogRooms.length > 0 && (
              <Select
                size="small"
                displayEmpty
                value={selectedRoomTypeId}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onRoomTypeChange(e.target.value)}
                error={!isMapped}
                sx={{ fontSize: 12, maxWidth: 320 }}
              >
                <MenuItem value="" disabled>
                  <Typography variant="body2" color="text.disabled">
                    Select room type…
                  </Typography>
                </MenuItem>
                {catalogRooms.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </Select>
            )}
            {existingLabel && (
              <Stack direction="row" alignItems="center" gap={0.5}>
                <EventBusyOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Existing: {existingLabel}
                </Typography>
              </Stack>
            )}
          </Stack>
          <Chip label={pluralizeToString('date range', room.dates.length)} size="small" sx={{ bgcolor: 'grey.200' }} />
          <Box>
            <Button size="small" onClick={handleToggleAll}>
              {allSelected ? 'Deselect all' : 'Select all dates'}
            </Button>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Stack divider={<Divider />}>
          {room.dates.map((date, dateIndex) => (
            <Stack key={dateIndex} sx={{ pl: 5, pr: 2, py: 0.75 }} gap={0.5}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Checkbox
                  checked={selected[dateIndex] ?? false}
                  onChange={(e) => onChange(dateIndex, e.target.checked)}
                  size="small"
                />
                <Typography variant="body2" color="text.secondary">
                  {formatDateRange(date.start, date.end)}
                </Typography>
                <Chip
                  label={pluralizeToString('night', countNights(date.start, date.end))}
                  size="small"
                  sx={{ bgcolor: 'warning.50', color: 'warning.dark', fontSize: 11 }}
                />
              </Stack>
              {catalogRoomRates.length > 0 && selected[dateIndex] && (
                <Stack gap={0.5} sx={{ pl: 4 }}>
                  {catalogRoomRates.map((rate, rateIndex) => {
                    const conflict = rateConflicts[dateIndex]?.[rateIndex] ?? { status: 'clear' as const };
                    const resolution = rateResolutions[`${dateIndex}-${rate.id}`] ?? null;
                    return (
                      <StopsellRateRow
                        key={rate.id}
                        rateName={rate.name}
                        rateConflict={conflict}
                        resolution={resolution}
                        onResolve={(status) => onResolve(dateIndex, rate.id, status)}
                        onUndo={() => onUndo(dateIndex, rate.id)}
                      />
                    );
                  })}
                </Stack>
              )}
            </Stack>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
